import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useAnalytics, useResponsiveBreakPoints } from '@restworld/utils-common';
import { useWizard } from '@restworld/utils-common';
import { Heading, Button } from '@restworld/ui-ds-base';
import { useWorkersService } from '@restworld/data-services';

import { WizardStepProps } from '../../worker-application-wizard.types';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { CoverLetterEntry } from '../../../cover-letter-application';
import { stepsMapping } from '../form-inputs.hooks';

export const WizardFifthStep: React.FunctionComponent<WizardStepProps> = () => {
  const [entryTime, setEntryTime] = React.useState<number>(Date.now());
  const [, setLoading] = React.useState(false);
  const { nextStep, goToStep, activeStep } = useWizard();
  const { isDesktop } = useResponsiveBreakPoints();
  const { workerId, setSkippedSteps, setFourthStepDecision, setOverlayModalContent } = useWorkerApplicationCxt();
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const workerService = useWorkersService();

  const updateWorkersHasPastExperience = React.useCallback(
    async (hasPastExperience: boolean) => {
      try {
        setLoading(true);
        await workerService.updateWorker({
          workerId,
          params: {
            ...{
              new_to_sector_having_no_experience: hasPastExperience,
            },
          },
        });
      } catch (error) {
        console.error('Error updating worker has past experience field: new_to_sector_having_no_experience', error);
      } finally {
        setLoading(false);
      }
    },
    [workerId, workerService]
  );

  React.useEffect(() => {
    setEntryTime(Date.now());
  }, [activeStep]);

  const getElapsedTime = React.useCallback(() => {
    return (Date.now() - entryTime) / 1000;
  }, [entryTime]);

  const isSpontaneousEntry = React.useMemo(() => router.query?.['isSpontaneousEntry'] === 'true', [router.query]);
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6 max-w-sm">
      <Heading size={isDesktop ? 2 : 4} weight="bold">
        {t('onboarding-page:have_you_already_had_work_experience')}
      </Heading>
      <div className="flex gap-2">
        <Button
          variant="secondary-neutral"
          className={'w-1/2'}
          onClick={() => {
            if (isSpontaneousEntry) {
              setOverlayModalContent({
                key: 'cover-letter-entry',
                visible: true,
                content: (
                  <CoverLetterEntry
                    restaurantID={router.query.id as string}
                    workerId={workerId}
                    closeEntryPoint={() => setOverlayModalContent({ visible: false })}
                  />
                ),
              });
              return;
            }
            updateWorkersHasPastExperience(false);
            setFourthStepDecision('skip');
            setSkippedSteps((p) => (!p.includes(5) ? [...p, 5] : [...p]));
            goToStep(6);
          }}
          testId="no-experience-btn"
        >
          No
        </Button>
        <Button
          variant="primary"
          className={'w-1/2'}
          onClick={() => {
            trackEvent('questionnaire_progress', {
              step_number: activeStep,
              step_name: stepsMapping[activeStep as number],
              time_spent: getElapsedTime(),
              next_action: 'continue',
              data: {
                has_work_experience: true,
              },
            });
            updateWorkersHasPastExperience(true);
            setFourthStepDecision('no-skip');
            setSkippedSteps((p) => [...p].filter((v) => v !== 5));
            nextStep();
          }}
          testId="yes-experience-btn"
        >
          Si
        </Button>
      </div>
    </div>
  );
};
export default WizardFifthStep;
